import { useEffect, useRef } from 'react'
import { useScrollContext } from '@/shared/provider/scroll-context/scroll-context.tsx'
import type { SectionId } from '@/teams/portfolio/types/section-id.ts'

const INTERSECTION_THRESHOLD = 0.85

/**
 * Observes a section and updates the URL when it becomes visible
 * Uses query parameter instead of hash to avoid browser auto-scroll
 */
export function useSectionObserver(sectionId: SectionId) {
	const sectionRef = useRef<HTMLElement>(null)
	const scrollContext = useScrollContext()

	useEffect(() => {
		const section = sectionRef.current
		if (!section) {
			return
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					// Skip URL update during programmatic scroll
					if (scrollContext.isScrolling) {
						return
					}

					const params = new URLSearchParams(window.location.search)
					const currentSection = params.get('section')

					if (currentSection !== sectionId) {
						params.set('section', sectionId)
						const newUrl = `${window.location.pathname}?${params.toString()}`
						history.replaceState(history.state, '', newUrl)
					}
				}
			},
			{
				threshold: INTERSECTION_THRESHOLD,
			},
		)
		observer.observe(section)

		return () => {
			observer.disconnect()
		}
	}, [sectionId, scrollContext])

	return sectionRef
}
